import React, { useMemo, useCallback, useReducer, useEffect } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';

import Loading from 'components/bronson/Loading';
import Button from 'components/bronson/Button';
import Input from 'components/bronson/Input';
import Traductor from 'components/Traductor';
import Modal from 'components/batches/Details/DetailsModal';

import { notificationTypeEnum } from 'enums/NotificactionTypeEnum';
import { disableHTMLOverflow } from 'Functions';
import Globals from '../Globals';

import privacy from 'json/privacy.js';
import policy from 'json/policy.js';

import UserService from 'services/UserService';
import { UserContext } from 'contexts/UserContext';

const initialRegisterState = {
    loading: true,
    modalVisible: null,
    user: null,
};

const registerReducer = (state, { type, payload }) => {
    switch (type) {
        case 'SET_USER':
            return { ...state, user: payload, loading: false };
        case 'SET_LOADING':
            return { ...state, loading: payload };
        case 'SET_MODAL':
            return { ...state, modalVisible: payload };
        default:
            return state;
    }
};

const RegisterModuleComponent = ({
    setNotification,
    setRenderModuleType,
    cookies,
    handleLoginUser,
}) => {
    const token = useMemo(
        () => cookies.get(Globals.COOKIES.JWT)?.AccessToken,
        [cookies]
    );
    console.log(token, 'REGISTER');
    const t = useMemo(() => new Traductor(), []);
    const [{ loading, modalVisible, user }, dispatch] = useReducer(
        registerReducer,
        initialRegisterState
    );

    useEffect(() => {
        disableHTMLOverflow(modalVisible);
    }, [modalVisible]);

    const fetchUser = useCallback(async () => {
        try {
            const response = await new UserService().getUser(token, true);
            if (response && response.statusCode === 200) {
                const parsedUser = JSON.parse(response.body.Result);
                //Fill form with data
                return dispatch({
                    type: 'SET_USER',
                    payload: parsedUser,
                });
            } else if (response && response.statusCode === 520) {
                setNotification(
                    notificationTypeEnum.ERROR,
                    'Este usuario ya está registrado en el aplicativo.'
                );
            } else if (response && response.statusCode === 401) {
                setNotification(
                    notificationTypeEnum.ERROR,
                    'El token ya no es valido.'
                );
            } else {
                setNotification(
                    notificationTypeEnum.ERROR,
                    t.getTraduction('RegisterNotifications').LoadDataFailed()
                );
            }
        } catch (error) {
            console.log(error);
            setNotification(
                notificationTypeEnum.ERROR,
                t.getTraduction('RegisterNotifications').LoadDataFailed
            );
        }
        setRenderModuleType(1);
    }, [setNotification, setRenderModuleType, t, token]);

    useEffect(() => {
        if (!user) {
            if (!token) {
                setNotification(
                    notificationTypeEnum.ERROR,
                    t.getTraduction('RegisterNotifications').TokenExpired
                );
                setRenderModuleType(1);
            } else {
                fetchUser(token);
            }
        }
    }, [token, setRenderModuleType, setNotification, fetchUser, t, user]);

    const onOpenModal = useCallback((payload = null) => {
        dispatch({ type: 'SET_MODAL', payload });
    }, []);

    const inputOptions = useMemo(
        () => ({
            name: {
                id: 'name',
                name: 'name',
                event: true,
                type: 'text',
                label: t.getTraduction('Name') + '*',
                readOnly: true,
            },
            firstSurname: {
                id: 'firstSurname',
                name: 'firstSurname',
                event: true,
                type: 'text',
                label: t.getTraduction('Surname') + '*',
                readOnly: true,
            },
            secondSurname: {
                id: 'secondSurname',
                name: 'secondSurname',
                event: true,
                type: 'text',
                label: t.getTraduction('Surname2'),
                readOnly: true,
            },
            email: {
                id: 'email',
                name: 'email',
                event: true,
                type: 'email',
                label: t.getTraduction('PlaceHolderEmail') + '*',
                readOnly: true,
            },
            phone: {
                id: 'phone',
                name: 'phone',
                event: true,
                type: 'tel',
                label: t.getTraduction('PlaceHolderPhone'),
            },
            password: {
                id: 'password',
                name: 'password',
                event: true,
                type: 'password',
                label: t.getTraduction('PlaceHolderPassword') + '*',
                backButton: {
                    type: 'control-eye',
                },
            },
            repeatedPassword: {
                id: 'repeatedPassword',
                name: 'repeatedPassword',
                event: true,
                type: 'password',
                label: t.getTraduction('PasswordNewRepeat') + '*',
                backButton: {
                    type: 'control-eye',
                },
            },
            documentId: {
                id: 'documentId',
                type: 'dni',
                name: 'documentId',
                event: true,
                label: t.getTraduction('PlaceHolderDni') + '*',
                errorMessage: t.getTraduction('RegisterFormErrors').dni,
            },
            legal: {
                id: 'legal',
                type: 'checkbox',
                name: 'legal',
                event: true,
                text: {
                    text: (
                        <p>
                            {t.getTraduction('TermsAccept')}{' '}
                            <a
                                href="##"
                                onClick={e => {
                                    e.preventDefault();
                                    onOpenModal({
                                        content: (
                                            <p
                                                style={{
                                                    whiteSpace: 'pre-line',
                                                    textAlign: 'justify',
                                                }}
                                            >
                                                {policy.content}
                                            </p>
                                        ),
                                        title: policy.title,
                                    });
                                }}
                            >
                                {t.getTraduction('LegalAdvice')}
                            </a>
                            . {t.getTraduction('ContactUs')}
                        </p>
                    ),
                },
            },
        }),
        [onOpenModal, t]
    );

    const submitButtonOptions = useMemo(
        () => ({
            text: t.getTraduction('SubmitRegister'),
            value: 'Solicitar',
            type: 'submit',
            class: 'u-block-center',
        }),
        [t]
    );

    const onChange = useCallback((e, callback) => {
        const {
            target: { type, files, checked, value, name },
        } = e;
        switch (type) {
            case 'checkbox':
                return callback(name, checked);
            case 'file':
                return callback(name, files[0]);
            default:
                return callback(name, value);
        }
    }, []);

    const onChangeLoadingStatus = useCallback(
        payload => dispatch({ type: 'SET_LOADING', payload }),
        []
    );

    const onSubmit = useCallback(
        async (form, bag) => {
            onChangeLoadingStatus(true);
            try {
                const result = await new UserService().register(token, {
                    ...form,
                    Profile: user.Profile,
                });
                onChangeLoadingStatus(false);
                console.log(result, 'Register response');
                if (result?.body?.Result) {
                    const cookieJWT = JSON.parse(result.body.Result);
                    setNotification(
                        notificationTypeEnum.SUCCESS,
                        t.getTraduction('RegisterNotifications').RegisterSuccess
                    );
                    bag.resetForm(); //Reset form if register is succesful

                    await handleLoginUser(user, cookieJWT);
                } else {
                    setNotification(
                        notificationTypeEnum.ERROR,
                        t.getTraduction('RegisterNotifications').RegisterFailed
                    );
                }
            } catch (error) {
                console.log(error);
                setNotification(
                    notificationTypeEnum.ERROR,
                    t.getTraduction('RegisterNotifications').RegisterFailed
                );
            } finally {
                onChangeLoadingStatus(false);
            }
        },
        [
            onChangeLoadingStatus,
            setNotification,
            t,
            token,
            user,
            handleLoginUser,
        ]
    );

    const renderError = useCallback(
        (error, touched, id) =>
            error && touched ? (
                <p data-testid={`error-${id}`} className="c-error-message">
                    {error}
                </p>
            ) : null,
        []
    );

    const renderInput = useCallback(
        (options, value, error, touched, setFieldValue, onBlur) => (
            <div
                key={options.id}
                data-testid={options.id}
                className="o-fieldset__row"
            >
                <Input
                    options={options}
                    onChange={e =>
                        options.readOnly ? null : onChange(e, setFieldValue)
                    }
                    onBlur={onBlur}
                    value={value}
                />
                {renderError(error, touched, options.id)}
            </div>
        ),
        [onChange, renderError]
    );

    return (
        <>
            <Loading ariaBusy={loading} />
            <Formik
                enableReinitialize
                initialValues={{
                    documentId: '',
                    name: user?.Name || '',
                    firstSurname: user?.Surname || '',
                    secondSurname: user?.Surname2 || '',
                    email: user?.Email || '',
                    phone: user?.Phone || '',
                    password: '',
                    repeatedPassword: '',
                    legal: false,
                }}
                onSubmit={onSubmit}
                validationSchema={Yup.object().shape({
                    name: Yup.string().required(
                        t.getTraduction('RegisterFormErrors').required
                    ),
                    firstSurname: Yup.string().required(
                        t.getTraduction('RegisterFormErrors').required
                    ),
                    secondSurname: Yup.string(),
                    email: Yup.string()
                        .required(
                            t.getTraduction('RegisterFormErrors').required
                        )
                        .email(t.getTraduction('RegisterFormErrors').email),
                    phone: Yup.string()
                        .required(
                            t.getTraduction('RegisterFormErrors').required
                        )
                        .matches(
                            /^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/,
                            t.getTraduction('RegisterFormErrors').phone
                        ),
                    password: Yup.string()
                        .required(
                            t.getTraduction('RegisterFormErrors').required
                        )
                        .matches(
                            /\d/,
                            t.getTraduction('RegisterFormErrors').password
                                .numberRequired
                        )
                        .matches(
                            /[A-Z]/,
                            t.getTraduction('RegisterFormErrors').password
                                .capsRequired
                        )
                        .min(
                            8,
                            t
                                .getTraduction('RegisterFormErrors')
                                .password.minLength(8)
                        ),
                    repeatedPassword: Yup.string()
                        .required(
                            t.getTraduction('RegisterFormErrors').required
                        )
                        .oneOf(
                            [Yup.ref('password'), null],
                            t.getTraduction('RegisterFormErrors').password
                                .matchRequired
                        ),
                    documentId: Yup.string()
                        .required(
                            t.getTraduction('RegisterFormErrors').required
                        )
                        .oneOf(
                            [user?.DocumentId, user?.DocumentId?.toLowerCase()],
                            t.getTraduction('RegisterFormErrors').dni
                                .matchRequired
                        ),
                    legal: Yup.bool().oneOf(
                        [true],
                        t.getTraduction('RegisterFormErrors').termsOfService
                    ),
                })}
            >
                {({
                    values,
                    handleSubmit,
                    setFieldValue,
                    errors,
                    isValid,
                    touched,
                    handleBlur,
                }) => (
                    <>
                        <div>
                            <p
                                className="u-h6 u-mv"
                                style={{ whiteSpace: 'pre-line' }}
                            >
                                {t.getTraduction('RegisterFormLabel')}
                            </p>
                        </div>
                        {Object.keys(values).map(name => {
                            return renderInput(
                                {
                                    ...inputOptions[name],
                                    className:
                                        errors[name] && touched[name]
                                            ? 'is-error'
                                            : '',
                                },
                                values[name],
                                errors[name],
                                touched[name],
                                setFieldValue,
                                handleBlur
                            );
                        })}
                        <div className="o-split">
                            <Button
                                options={submitButtonOptions}
                                onClick={handleSubmit}
                            />
                        </div>
                    </>
                )}
            </Formik>
            <Modal
                visible={modalVisible}
                content={modalVisible?.content}
                title={modalVisible?.title}
                onClose={e => {
                    e.preventDefault();
                    onOpenModal(null);
                }}
                onActionPress={e => {
                    e.preventDefault();
                    onOpenModal(null);
                }}
                rightButton={t.getTraduction('Close')}
                large={true}
            />
        </>
    );
};
export default RegisterModuleComponent;
